import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Tag,
    Button,
    Space,
    Modal,
    Typography,
    Empty,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Card,
    message
} from 'antd';
import { 
    EyeOutlined, 
    SearchOutlined, 
    FilterOutlined,
    UserOutlined,
    MailOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ISurveyResponse, ISurvey } from '../../services/nestjs-base-api/types';
import { useDeleteSurveyResponseMutation, useDeleteSurveyResponsesMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import ResponseDetail from './ResponseDetail';
import dayjs from 'dayjs';
import { ResponsiveTable } from '../../admin/components/ResponsiveTable';

const { Text } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ResponsesListProps {
    responses: ISurveyResponse[];
    loading: boolean;
    survey: ISurvey;
    onResponseDeleted?: () => void;
}

const ResponsesList: React.FC<ResponsesListProps> = ({ responses, loading, survey, onResponseDeleted }) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [selectedResponse, setSelectedResponse] = useState<ISurveyResponse | null>(null);
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
    
    const [deleteResponse] = useDeleteSurveyResponseMutation();
    const [deleteResponses] = useDeleteSurveyResponsesMutation();

    const getStatusColor = (response: ISurveyResponse) => {
        // All responses in the API are completed (they have submittedAt)
        if (response.questionResponses.length === 0) {
            return 'warning'; // No responses - abandoned
        }
        return 'success'; // Has responses - completed
    };

    const getStatus = (response: ISurveyResponse) => {
        if (response.questionResponses.length === 0) {
            return 'abandoned';
        }
        return 'completed';
    };

    const formatDuration = (response: ISurveyResponse) => {
        // Since we only have submittedAt and no startedAt, we can't calculate duration
        // We could estimate based on response times if they were available
        return '-';
    };

    const handleViewResponse = (response: ISurveyResponse) => {
        setSelectedResponse(response);
        setIsDetailModalVisible(true);
    };

    const handleDeleteSelected = () => {
        if (selectedRowKeys.length === 0) {
            message.warning(intl.formatMessage({ id: 'survey.results.selectResponsesToDelete' }));
            return;
        }

        Modal.confirm({
            title: intl.formatMessage({ id: 'survey.results.confirmDelete.title' }),
            content: intl.formatMessage(
                { id: 'survey.results.confirmDelete.content' },
                { count: selectedRowKeys.length }
            ),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'common.delete' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancel' }),
            onOk: async () => {
                try {
                    await deleteResponses({
                        jwtToken: user!.jwtToken,
                        surveyId: survey.id,
                        responseIds: selectedRowKeys
                    }).unwrap();
                    
                    message.success(
                        intl.formatMessage(
                            { id: 'survey.results.deleteSuccess' },
                            { count: selectedRowKeys.length }
                        )
                    );
                    setSelectedRowKeys([]);
                    
                    // Trigger refetch
                    if (onResponseDeleted) {
                        onResponseDeleted();
                    }
                } catch (error) {
                    message.error(intl.formatMessage({ id: 'survey.results.deleteError' }));
                }
            },
        });
    };

    const handleDeleteSingle = (responseId: number) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'survey.results.confirmDelete.title' }),
            content: intl.formatMessage(
                { id: 'survey.results.confirmDelete.singleContent' }
            ),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'common.delete' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancel' }),
            onOk: async () => {
                try {
                    await deleteResponse({
                        jwtToken: user!.jwtToken,
                        surveyId: survey.id,
                        responseId: responseId
                    }).unwrap();
                    
                    message.success(
                        intl.formatMessage({ id: 'survey.results.deleteSingleSuccess' })
                    );
                    
                    // Remove from selected if it was selected
                    setSelectedRowKeys(prev => prev.filter(id => id !== responseId));
                    
                    // Trigger refetch
                    if (onResponseDeleted) {
                        onResponseDeleted();
                    }
                    
                } catch (error) {
                    message.error(intl.formatMessage({ id: 'survey.results.deleteError' }));
                }
            },
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys as number[]);
        },
    };

    const filteredResponses = responses.filter(response => {
        const matchesSearch = !searchText || 
            (response.participantEmail?.toLowerCase().includes(searchText.toLowerCase())) ||
            (response.participantIdentifier?.toLowerCase().includes(searchText.toLowerCase()));
        
        const responseStatus = getStatus(response);
        const matchesStatus = statusFilter === 'all' || responseStatus === statusFilter;
        
        const matchesDate = !dateRange || (
            dayjs(response.submittedAt).isAfter(dateRange[0].startOf('day')) &&
            dayjs(response.submittedAt).isBefore(dateRange[1].endOf('day'))
        );

        return matchesSearch && matchesStatus && matchesDate;
    });

    const columns: ColumnsType<ISurveyResponse> = [
        {
            title: <FormattedMessage id="survey.results.table.participant" />,
            key: 'participant',
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    {record.participantIdentifier ? (
                        <Text strong>
                            <UserOutlined style={{ marginRight: 8 }} />
                            {record.participantIdentifier}
                        </Text>
                    ) : (
                        <Text type="secondary">
                            <UserOutlined style={{ marginRight: 8 }} />
                            <FormattedMessage id="survey.results.table.anonymous" />
                        </Text>
                    )}
                    {record.participantEmail && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <MailOutlined style={{ marginRight: 4 }} />
                            {record.participantEmail}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="survey.results.table.status" />,
            key: 'status',
            width: 120,
            render: (_, record) => {
                const status = getStatus(record);
                return (
                    <Tag color={getStatusColor(record)}>
                        <FormattedMessage id={`survey.results.status.${status}`} />
                    </Tag>
                );
            },
        },
        {
            title: <FormattedMessage id="survey.results.table.responses" />,
            key: 'responseCount',
            width: 120,
            render: (_, record) => (
                <Text>
                    {record.questionResponses.length} <FormattedMessage id="survey.results.table.answered" />
                </Text>
            ),
        },
        {
            title: <FormattedMessage id="survey.results.table.submittedAt" />,
            dataIndex: 'submittedAt',
            key: 'submittedAt',
            width: 180,
            render: (submittedAt: string) => (
                <Space direction="vertical" size={0}>
                    <Text>{dayjs(submittedAt).format('MMM D, YYYY')}</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {dayjs(submittedAt).format('h:mm A')}
                    </Text>
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="survey.results.table.duration" />,
            key: 'duration',
            width: 100,
            render: (_, record) => (
                <Text>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {formatDuration(record)}
                </Text>
            ),
        },
        {
            title: <FormattedMessage id="survey.results.table.actions" />,
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewResponse(record)}
                        size="small"
                    >
                        <FormattedMessage id="action.view" />
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteSingle(record.id)}
                        size="small"
                    >
                        <FormattedMessage id="action.delete" />
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card size="small">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Search
                                placeholder="Search by email or participant ID"
                                allowClear
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                prefix={<SearchOutlined />}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Select
                                value={statusFilter}
                                onChange={setStatusFilter}
                                style={{ width: '100%' }}
                                suffixIcon={<FilterOutlined />}
                            >
                                <Option value="all">
                                    <FormattedMessage id="survey.results.filter.allStatuses" />
                                </Option>
                                <Option value="completed">
                                    <FormattedMessage id="survey.results.status.completed" />
                                </Option>
                                <Option value="started">
                                    <FormattedMessage id="survey.results.status.started" />
                                </Option>
                                <Option value="abandoned">
                                    <FormattedMessage id="survey.results.status.abandoned" />
                                </Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <RangePicker
                                value={dateRange}
                                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                                style={{ width: '100%' }}
                                placeholder={['Start date', 'End date']}
                            />
                        </Col>
                    </Row>
                    {selectedRowKeys.length > 0 && (
                        <Row style={{ marginTop: 16 }}>
                            <Col span={24}>
                                <Space>
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleDeleteSelected}
                                    >
                                        <FormattedMessage 
                                            id="survey.results.deleteSelected" 
                                            values={{ count: selectedRowKeys.length }}
                                        />
                                    </Button>
                                    <Button onClick={() => setSelectedRowKeys([])}>
                                        <FormattedMessage id="common.clearSelection" />
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    )}
                </Card>

                <ResponsiveTable
                    columns={columns}
                    dataSource={filteredResponses}
                    loading={loading}
                    rowKey="id"
                    rowSelection={rowSelection}
                    mobileColumns={[
                        {
                            title: 'Response',
                            key: 'response',
                            render: (_, record: ISurveyResponse) => (
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div>
                                        <strong>
                                            {record.participantEmail || (
                                                <FormattedMessage
                                                    id="surveyResults.responses.anonymousRespondent"
                                                    defaultMessage="Anonymous Respondent"
                                                />
                                            )}
                                        </strong>
                                        <Tag color={getStatusColor(record)} style={{ marginLeft: 8 }}>
                                            <FormattedMessage
                                                id="surveyResults.responses.completed"
                                                defaultMessage="Completed"
                                            />
                                        </Tag>
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                        {dayjs(record.submittedAt).format('MMM DD, YYYY HH:mm')}
                                    </Text>
                                </Space>
                            ),
                        },
                        {
                            title: 'Actions',
                            key: 'actions',
                            width: 100,
                            render: (_, record: ISurveyResponse) => (
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => handleViewResponse(record)}
                                    size="small"
                                >
                                    <FormattedMessage
                                        id="surveyResults.responses.viewDetails"
                                        defaultMessage="View"
                                    />
                                </Button>
                            ),
                        },
                    ]}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => (
                            <FormattedMessage
                                id="survey.results.table.pagination"
                                values={{
                                    start: range[0],
                                    end: range[1],
                                    total
                                }}
                            />
                        ),
                    }}
                    locale={{
                        emptyText: (
                            <Empty
                                description={<FormattedMessage id="survey.results.table.noResponses" />}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        ),
                    }}
                />
            </Space>

            <Modal
                title={
                    <Space>
                        <EyeOutlined />
                        <FormattedMessage id="survey.results.modal.responseDetails" />
                    </Space>
                }
                open={isDetailModalVisible}
                onCancel={() => setIsDetailModalVisible(false)}
                footer={null}
                width={800}
                destroyOnClose
            >
                {selectedResponse && (
                    <ResponseDetail
                        response={selectedResponse}
                        survey={survey}
                    />
                )}
            </Modal>
        </>
    );
};

export default ResponsesList;